/**
 * auth-ui.ts - UI updates for authentication state
 */

import type { User } from '../../types/auth';

/**
 * Update UI based on authentication state
 * @param user - Current user or null if not authenticated
 */
export function updateAuthUI(user: User | null): void {
  // Check for demo mode - treat it like logged in for UI purposes
  const urlParams = new URLSearchParams(window.location.search);
  const isDemoMode = urlParams.has('demo');

  // Check for redirect FIRST - this doesn't require any DOM elements
  if (user) {
    const currentPath = window.location.pathname;
    if (currentPath === '/') {
      window.location.href = '/mytournaments';
      return; // Exit early, redirect will happen
    }
  }

  const authStatus = document.getElementById('auth-status');
  const createTournamentBtn = document.getElementById('create-tournament-btn');
  const userMenuAuthSection = document.getElementById('user-menu-auth-section');
  const userMenuGuestSection = document.getElementById('user-menu-guest-section');
  const landingPage = document.getElementById('landing-page');
  const tournamentsList = document.querySelector('.tournaments-list') as HTMLElement | null;
  const contextSwitcher = document.querySelector('.context-switcher') as HTMLElement | null;
  const i18n = window.i18n;

  if (!authStatus) return;

  if (user || isDemoMode) {

    // Logged in or demo mode - show appropriate greeting
    if (isDemoMode && !user) {
      // Demo mode without login - clear status
      authStatus.innerHTML = '';
    } else if (user) {
      // Real user - show greeting
      const hello = i18n ? i18n.t('header.hello') : 'Hello';
      authStatus.innerHTML = `<span class="user-greeting"><span class="greeting-text">${hello}, </span><strong>${user.username}</strong></span>`;
    }

    // Create tournament button - hide for demo, show for real users
    if (createTournamentBtn) {
      if (isDemoMode && !user) {
        createTournamentBtn.style.display = 'none';
      } else {
        // Keep the icon-only button from HTML
        createTournamentBtn.onclick = () => {
          (window as any).showCreateTournament();
        };
      }
    }

    // Show authenticated user menu only for real users, not demo
    if (userMenuAuthSection) {
      if (isDemoMode && !user) {
        userMenuAuthSection.style.display = 'none';
      } else {
        userMenuAuthSection.style.display = 'block';
      }

      // Add admin link for admin users
      if (user) {
        console.log('[Auth] User role:', user.role);
        if (user.role === 'admin') {
        console.log('[Auth] User is admin - adding admin menu item');
        let adminLink = userMenuAuthSection.querySelector('.admin-menu-item');
        if (!adminLink) {
          const accountButton = userMenuAuthSection.querySelector('button:first-of-type');
          if (accountButton) {
            const adminButton = document.createElement('button');
            adminButton.className = 'user-menu-item admin-menu-item';
            adminButton.onclick = () => {
              window.location.href = '/admin';
              toggleUserMenu();
            };
            adminButton.innerHTML = '<span>Admin Dashboard</span>';
            accountButton.parentNode?.insertBefore(adminButton, accountButton);
            console.log('[Auth] Admin menu item added successfully');
          } else {
            console.warn('[Auth] Could not find account button to insert admin link');
          }
        } else {
          console.log('[Auth] Admin menu item already exists');
        }
      } else {
        console.log('[Auth] User is not admin - skipping admin menu item');
      }
      }
    }

    // Hide guest menu section for real users, show for demo
    if (userMenuGuestSection) {
      if (isDemoMode && !user) {
        userMenuGuestSection.style.display = 'block';
      } else {
        userMenuGuestSection.style.display = 'none';
      }
    }

    // Hide landing page, show tournaments list (hide context switcher for demo)
    if (landingPage) landingPage.style.display = 'none';
    document.body.classList.remove('landing-page-active');
    if (tournamentsList) tournamentsList.style.display = 'block';
    if (contextSwitcher) {
      if (isDemoMode && !user) {
        contextSwitcher.style.display = 'none';
      } else {
        contextSwitcher.style.display = 'block';
      }
    }
  } else {
    // Not logged in - clear auth status (no login/register buttons in main header anymore)
    authStatus.innerHTML = '';

    // Check if we're on mytournaments page - allow creating demo tournaments
    const isMyTournamentsPage = window.location.pathname.includes('/mytournaments');

    // Create tournament button behavior depends on page
    if (createTournamentBtn) {
      if (isMyTournamentsPage) {
        // On mytournaments, allow creating demo tournaments without login
        createTournamentBtn.onclick = () => {
          (window as any).showCreateTournament();
        };
      } else {
        // On other pages, trigger auth modal
        createTournamentBtn.onclick = () => {
          (window as any).openAuthModal('register');
        };
      }
    }

    // Hide authenticated user menu, show guest menu
    if (userMenuAuthSection) {
      userMenuAuthSection.style.display = 'none';
    }

    if (userMenuGuestSection) {
      userMenuGuestSection.style.display = 'block';
    }

    // Show landing page, hide tournaments list and context switcher
    // Note: demo mode is handled in the logged-in section above
    if (landingPage) {
      landingPage.style.display = 'block';
      document.body.classList.add('landing-page-active');
    }

    // On mytournaments page without landing page, show welcome message instead of hiding
    if (isMyTournamentsPage && !landingPage) {
      // Show the tournaments list section but with welcome content
      if (tournamentsList) tournamentsList.style.display = 'block';
      if (contextSwitcher) contextSwitcher.style.display = 'none';
      showGuestWelcomeMessage();
    } else {
      // Normal behavior - hide tournaments list
      if (tournamentsList) tournamentsList.style.display = 'none';
      if (contextSwitcher) contextSwitcher.style.display = 'none';
    }
  }
}

/**
 * Show a welcome message for non-logged in users on mytournaments page
 * Also loads any demo tournaments from sessionStorage
 */
async function showGuestWelcomeMessage(): Promise<void> {
  const container = document.getElementById('tournaments-container');
  if (!container) return;

  const i18n = window.i18n;
  const t = (key: string, fallback: string) => i18n?.t(key) || fallback;

  // Check for demo tournaments in sessionStorage
  const demoTournamentIds: string[] = JSON.parse(sessionStorage.getItem('demoTournaments') || '[]');

  // If there are demo tournaments, load and display them
  if (demoTournamentIds.length > 0) {
    container.innerHTML = `<p class="loading-message">${t('common.loading', 'Loading...')}</p>`;

    try {
      // Fetch all demo tournaments
      const tournaments = await Promise.all(
        demoTournamentIds.map(async (id) => {
          try {
            const response = await fetch(`/api/tournaments/${id}`, { credentials: 'include' });
            if (response.ok) {
              return await response.json();
            }
            return null;
          } catch {
            return null;
          }
        })
      );

      // Filter out null (deleted/expired) tournaments and update sessionStorage
      const validTournaments = tournaments.filter(t => t !== null);
      const validIds = validTournaments.map(t => t.id);
      sessionStorage.setItem('demoTournaments', JSON.stringify(validIds));

      if (validTournaments.length > 0) {
        // Display tournaments using the list card format
        if (window.displayTournamentsList) {
          window.displayTournamentsList(validTournaments);
        }

        // Add demo banner at the top
        const main = container.closest('main');
        if (main) {
          const existingBanner = document.getElementById('demo-session-banner');
          if (!existingBanner) {
            const banner = document.createElement('div');
            banner.id = 'demo-session-banner';
            banner.className = 'demo-tournament-banner';
            banner.innerHTML = `
              <div class="demo-banner-content">
                <div class="demo-banner-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div class="demo-banner-text">
                  <p class="demo-banner-title">${t('guest.session.title', 'Demo Session')}</p>
                  <p class="demo-banner-subtitle">${t('guest.session.subtitle', 'These tournaments are temporary and will be lost when you close your browser.')}</p>
                </div>
                <div class="demo-banner-actions">
                  <button class="btn btn-primary btn-sm" onclick="window.openAuthModal && window.openAuthModal('register')">
                    ${t('landing.demo.banner.signup', 'Sign Up Free')}
                  </button>
                </div>
              </div>
            `;
            main.insertBefore(banner, main.firstChild);
          }
        }
        return;
      }
    } catch (error) {
      console.error('Error loading demo tournaments:', error);
    }
  }

  // No demo tournaments - show welcome message
  container.innerHTML = `
    <div class="guest-welcome-message">
      <div class="guest-welcome-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
      </div>
      <h2>${t('guest.welcome.title', 'Welcome to nextround')}</h2>
      <p>${t('guest.welcome.subtitle', 'Create and manage chess tournaments with professional pairing algorithms')}</p>
      <div class="guest-welcome-actions">
        <button class="btn btn-primary btn-large" onclick="window.showCreateTournament && window.showCreateTournament()">
          ${t('guest.welcome.createTournament', 'Create Tournament')}
        </button>
      </div>
      <p class="guest-welcome-note">${t('guest.welcome.demoNote', 'No signup required. Tournament will be saved for this session.')}</p>
      <div class="guest-welcome-login">
        <span>${t('guest.welcome.haveAccount', 'Have an account?')}</span>
        <a href="#" onclick="event.preventDefault(); window.openAuthModal && window.openAuthModal('login');">${t('guest.welcome.login', 'Login')}</a>
        <span>${t('guest.welcome.or', 'or')}</span>
        <a href="#" onclick="event.preventDefault(); window.openAuthModal && window.openAuthModal('register');">${t('guest.welcome.signup', 'Sign up')}</a>
        <span>${t('guest.welcome.toSave', 'to save your tournaments')}</span>
      </div>
    </div>
  `;
}

/**
 * Toggle user menu dropdown visibility
 */
export function toggleUserMenu(): void {
  const dropdown = document.getElementById('user-menu-dropdown');
  const landingDropdown = document.getElementById('landing-user-menu-dropdown');

  // Toggle whichever dropdown exists on the current page
  if (dropdown) {
    const isVisible = dropdown.style.display === 'block';
    dropdown.style.display = isVisible ? 'none' : 'block';
  }

  if (landingDropdown) {
    const isVisible = landingDropdown.style.display === 'block';
    landingDropdown.style.display = isVisible ? 'none' : 'block';
  }
}

/**
 * Setup user menu click-outside handler
 */
export function setupUserMenuHandler(): void {
  // Close user menu when clicking outside
  document.addEventListener('click', function(event) {
    const userMenuContainers = document.querySelectorAll('.user-menu-container');
    const dropdown = document.getElementById('user-menu-dropdown');
    const landingDropdown = document.getElementById('landing-user-menu-dropdown');

    // Check if click was outside all user menu containers
    let clickedOutside = true;
    userMenuContainers.forEach(container => {
      if (container.contains(event.target as Node)) {
        clickedOutside = false;
      }
    });

    if (clickedOutside) {
      if (dropdown) {
        dropdown.style.display = 'none';
      }
      if (landingDropdown) {
        landingDropdown.style.display = 'none';
      }
    }
  });
}

// Export to window for onclick handlers and share.html
window.toggleUserMenu = toggleUserMenu;
window.updateAuthUI = updateAuthUI;
